import { useMemo } from 'react'

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Box, Paper, Skeleton, Stack, Tooltip, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'

import { MANUAL_INTERVENTION_CHART_HEIGHT } from './chartConfig'
import { ManualInterventionsChart } from './ManualInterventionsChart'
import { MetricSeries, ManualInterventionSeries } from './types'

interface Props {
  series: { label: string; color: string; metric?: MetricSeries | null }[]
  loading?: boolean
  titleColor: string
  info?: string
}

export const ManualInterventionsCard = ({ series, loading, titleColor, info }: Props) => {
  const chartSeries = useMemo<ManualInterventionSeries[]>(
    () =>
      series.map((s) => ({
        label: s.label,
        color: s.color,
        daily: s.metric?.daily ?? [],
      })),
    [series],
  )

  return (
    <Paper
      variant='outlined'
      sx={{ p: 2.5, borderRadius: 2, borderTop: `3px solid ${titleColor}` }}>
      <Stack direction='row' spacing={1} alignItems='center'>
        <Typography variant='subtitle1' fontWeight={600} sx={{ color: titleColor }}>
          Jobs with Handball, Wait Times or POC/POD Added Post-Booking
        </Typography>
        {info && (
          <Tooltip
            title={info}
            slotProps={{ tooltip: { sx: { whiteSpace: 'pre-line', maxWidth: 420 } } }}>
            <InfoOutlinedIcon fontSize='small' sx={{ color: 'text.disabled' }} />
          </Tooltip>
        )}
      </Stack>
      <Typography variant='body2' color='primary.main' mb={2}>
        Daily number of jobs
      </Typography>

      {loading ? (
        <Skeleton
          variant='rectangular'
          height={MANUAL_INTERVENTION_CHART_HEIGHT}
          sx={{ borderRadius: 1 }}
        />
      ) : (
        <ManualInterventionsChart series={chartSeries} />
      )}

      <Stack
        direction='row'
        spacing={1}
        alignItems='flex-start'
        sx={{
          mt: 2,
          p: 1.5,
          borderRadius: 2,
          bgcolor: alpha(titleColor, 0.04),
          border: 1,
          borderColor: alpha(titleColor, 0.2),
        }}>
        <Box sx={{ color: titleColor, display: 'flex', pt: 0.25 }}>
          <InfoOutlinedIcon fontSize='small' />
        </Box>
        <Box>
          <Typography variant='caption' color='text.secondary' display='block'>
            Daily count of jobs requiring post-booking operational input: handball or waiting time
            charges raised after the booking was taken, or proof of collection and delivery recorded
            by operations on the driver&apos;s behalf.
          </Typography>
        </Box>
      </Stack>
    </Paper>
  )
}
